import store from 'app/store';
import {
  ERROR_LOG_DEDUPE_WINDOW_MS,
  ERROR_LOG_MAX_PAGE_LENGTH,
  ERROR_LOG_MAX_REPORTS_PER_MINUTE,
  ERROR_LOG_MAX_TEXT_LENGTH,
  ERROR_LOG_MAX_URL_LENGTH,
  ERROR_LOG_MAX_USER_AGENT_LENGTH,
  HYDRATABLE_SLICES,
  SNAPSHOT_SCHEMA_VERSION
} from 'data/error-log-constants';
import { ErrorLogPayloadType, ReportErrorInputType } from 'types/error-log';
import axios from 'utils/api';
import { getRecentReduxActions } from 'utils/error-log/action-breadcrumbs-middleware';
import { capErrorLogBlob } from 'utils/error-log/cap-error-log-blob';
import { getConsoleSnapshot } from 'utils/error-log/console-recorder';
import {
  getLastInteraction,
  getRecentInteractions
} from 'utils/error-log/interaction-recorder';
import { captureScreenSnapshot } from 'utils/error-log/capture-screen-snapshot';
import { getCurrentPage } from 'utils/error-log/page-context';
import { buildReduxSnapshot } from 'utils/error-log/redux-snapshot';
import {
  formatErrorStack,
  normalizeError,
  truncateString
} from 'utils/error-log/serialize';

const RATE_WINDOW_MS = 60_000;

const recentReportTimestamps = new Map<string, number>();
let rateWindowStart = 0;
let rateWindowCount = 0;

function pruneRecentReports(now: number) {
  recentReportTimestamps.forEach((timestamp, key) => {
    if (now - timestamp > ERROR_LOG_DEDUPE_WINDOW_MS) {
      recentReportTimestamps.delete(key);
    }
  });
}

function isDuplicate(key: string, now: number): boolean {
  pruneRecentReports(now);
  const previous = recentReportTimestamps.get(key);
  recentReportTimestamps.set(key, now);
  return previous !== undefined && now - previous <= ERROR_LOG_DEDUPE_WINDOW_MS;
}

function isOverRateLimit(now: number): boolean {
  if (now - rateWindowStart > RATE_WINDOW_MS) {
    rateWindowStart = now;
    rateWindowCount = 0;
  }
  rateWindowCount += 1;
  return rateWindowCount > ERROR_LOG_MAX_REPORTS_PER_MINUTE;
}

function captureCallSiteStack(): string {
  const { stack } = new Error('errorModal call site');
  return stack ?? 'errorModal call site (no stack available)';
}

function resolveErrorStack(input: ReportErrorInputType): string | null {
  if (input.source === 'manualReport') {
    return null;
  }
  if (input.cause === undefined || input.cause === null) {
    return captureCallSiteStack();
  }
  return formatErrorStack(normalizeError(input.cause));
}

function buildPayload(input: ReportErrorInputType): ErrorLogPayloadType {
  const state = store.getState();
  const errorStack = resolveErrorStack(input);
  const emptyTrigger = {
    lastInteraction: null,
    recentInteractions: [],
    recentReduxActions: []
  };
  return {
    page: truncateString(getCurrentPage(state), ERROR_LOG_MAX_PAGE_LENGTH),
    url: truncateString(window.location.href, ERROR_LOG_MAX_URL_LENGTH),
    errorType: input.source,
    errorMessage: truncateString(input.message, ERROR_LOG_MAX_TEXT_LENGTH),
    explanation: input.explanation
      ? truncateString(input.explanation, ERROR_LOG_MAX_TEXT_LENGTH)
      : null,
    errorStack: errorStack
      ? truncateString(errorStack, ERROR_LOG_MAX_TEXT_LENGTH)
      : null,
    triggerAction: capErrorLogBlob(
      {
        lastInteraction: getLastInteraction(),
        recentInteractions: getRecentInteractions(),
        recentReduxActions: getRecentReduxActions()
      },
      emptyTrigger
    ),
    reduxState: capErrorLogBlob(buildReduxSnapshot(state), {
      slices: {},
      meta: {
        schemaVersion: SNAPSHOT_SCHEMA_VERSION,
        capturedAt: Date.now(),
        droppedSlices: [...HYDRATABLE_SLICES]
      }
    }),
    consoleSnapshot: capErrorLogBlob(getConsoleSnapshot(), []),
    userAgent: truncateString(
      navigator.userAgent,
      ERROR_LOG_MAX_USER_AGENT_LENGTH
    )
  };
}

function dedupeKey(payload: ErrorLogPayloadType): string {
  return [
    payload.errorType,
    payload.errorMessage,
    payload.errorStack?.slice(0, 300) ?? ''
  ].join('|');
}

export async function reportError(
  input: ReportErrorInputType
): Promise<boolean> {
  try {
    const now = Date.now();
    const payload = buildPayload(input);
    const isManual = input.source === 'manualReport';
    if (
      !isManual &&
      (isDuplicate(dedupeKey(payload), now) || isOverRateLimit(now))
    ) {
      return false;
    }
    const screenshot =
      input.screenshot !== undefined
        ? input.screenshot
        : await captureScreenSnapshot();
    await axios.post('error-logs', { ...payload, screenshot });
    return true;
  } catch {
    return false;
  }
}

export function resetErrorLogReporter() {
  recentReportTimestamps.clear();
  rateWindowStart = 0;
  rateWindowCount = 0;
}
